import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Check, Zap, RefreshCw, Receipt, ExternalLink, Coins, AlertTriangle, 
  Loader2, Phone, Users, Sparkles, HelpCircle, Building2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useAuth, API } from '../../App';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const InfoTooltip = ({ content }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help inline ml-1" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-xs">{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const BillingPage = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [organization, setOrganization] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [creditUsage, setCreditUsage] = useState(null);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [planLimits, setPlanLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [purchasingCredits, setPurchasingCredits] = useState(null);

  const sessionId = searchParams.get('session_id');
  const creditsSessionId = searchParams.get('credits_session_id');
  const cancelled = searchParams.get('cancelled');
  const creditsCancelled = searchParams.get('credits_cancelled');

  const canPurchase = user?.role === 'owner' || user?.role === 'admin' || user?.is_super_admin;

  useEffect(() => {
    fetchData();
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    
    if (creditsSessionId) {
      confirmCreditPurchase(creditsSessionId);
    }
    
    if (cancelled) {
      toast.error('Payment was cancelled');
    }
    
    if (creditsCancelled) {
      toast.error('Credit purchase was cancelled');
    }
  }, []);

  const fetchData = async () => {
    try {
      const [orgRes, transRes, creditRes, creditTransRes, limitsRes] = await Promise.all([
        axios.get(`${API}/organizations/current`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payments/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/credits/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/credits/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/channels/limits/check`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      setOrganization(orgRes.data);
      setTransactions(transRes.data);
      setCreditUsage(creditRes.data);
      setCreditTransactions(creditTransRes.data.transactions || []);
      setPlanLimits(limitsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmCreditPurchase = async (sessionId) => {
    setCheckingPayment(true);
    try {
      const response = await axios.post(`${API}/credits/confirm`, {
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        toast.success(`${response.data.credits_added} AI credits added to your account!`);
        fetchData();
      } else if (response.data.status === 'already_applied') {
        toast.info('Credits were already applied to your account');
      }
      
      window.history.replaceState({}, '', '/dashboard/billing');
    } catch (error) {
      toast.error('Failed to confirm credit purchase');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handlePurchaseCredits = async (packageId) => {
    if (!canPurchase) {
      toast.error('Only organization Owner and Admin can purchase credits');
      return;
    }
    
    setPurchasingCredits(packageId);
    try {
      const response = await axios.post(`${API}/credits/purchase`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start credit purchase';
      toast.error(message);
    } finally {
      setPurchasingCredits(null);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        fetchData();
        setCheckingPayment(false);
        window.history.replaceState({}, '', '/dashboard/billing');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setCheckingPayment(false);
    }
  };

  const handleUpgrade = async (plan) => {
    setUpgrading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    setOpeningPortal(true);
    try {
      const response = await axios.post(`${API}/billing/create-portal-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to open subscription management';
      toast.error(message);
    } finally {
      setOpeningPortal(false);
    }
  };

  // Updated plans to match new structure
  const plans = [
    {
      name: 'Starter',
      price: 49,
      id: 'starter',
      icon: Phone,
      features: [
        { text: '1 WhatsApp Channel', tooltip: 'Each channel connects one WhatsApp Business API number.' },
        { text: '3 Team Members' },
        { text: '1,000 messages/month' },
        { text: '2,000 AI credits/month', tooltip: 'Credits are used per AI interaction cycle.' },
        { text: 'All AI features included', highlight: true }
      ]
    },
    {
      name: 'Growth',
      price: 149,
      id: 'growth',
      popular: true,
      icon: Users,
      features: [
        { text: '3 WhatsApp Channels', tooltip: 'Each channel connects one WhatsApp Business API number.' },
        { text: '10 Team Members' },
        { text: '10,000 messages/month' },
        { text: '20,000 AI credits/month', tooltip: 'Credits are used per AI interaction cycle.' },
        { text: 'All AI features included', highlight: true }
      ]
    },
    {
      name: 'Scale',
      price: 499,
      id: 'scale',
      icon: Building2,
      features: [
        { text: '10 WhatsApp Channels', tooltip: 'Each channel connects one WhatsApp Business API number.' },
        { text: '30 Team Members' },
        { text: '50,000 AI credits/month', tooltip: 'Credits are used per AI interaction cycle.' },
        { text: 'All AI features included', highlight: true },
        { text: 'Priority support' }
      ]
    }
  ];

  // Map old plan names to new display names
  const getPlanDisplayName = (planKey) => {
    const mapping = {
      'trial': 'Trial',
      'starter': 'Starter',
      'professional': 'Growth', // Map old name to new
      'growth': 'Growth',
      'enterprise': 'Scale',
      'scale': 'Scale'
    };
    return mapping[planKey?.toLowerCase()] || planKey;
  };

  const currentPlan = organization?.subscription_plan || 'trial';
  const currentPlanDisplay = getPlanDisplayName(currentPlan);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Billing & Subscription</h1>
        <p className="text-muted-foreground">Manage your subscription and payment details</p>
      </div>

      {checkingPayment && (
        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-3">
          <RefreshCw className="w-5 h-5 animate-spin text-emerald-600" />
          <span className="text-emerald-700 dark:text-emerald-300">Checking payment status...</span>
        </div>
      )}

      {/* Current Plan + Usage Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold">{currentPlanDisplay}</p>
              <p className="text-muted-foreground">
                {currentPlan === 'trial' ? 'Trial period active' : 'Active subscription'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={currentPlan === 'trial' ? 'bg-yellow-500' : 'bg-emerald-600'}>
                {currentPlan === 'trial' ? 'Trial' : 'Active'}
              </Badge>
              {currentPlan !== 'trial' && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleManageSubscription}
                  disabled={openingPortal}
                  data-testid="manage-subscription-btn"
                >
                  {openingPortal ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Manage
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Usage Counters */}
          {planLimits && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t">
              {/* Channels Usage */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium">Channels</span>
                  <InfoTooltip content="Each channel connects one WhatsApp Business API number." />
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold">{planLimits.channels?.current || 0}</span>
                  <span className="text-muted-foreground">
                    / {planLimits.channels?.max === -1 ? '∞' : planLimits.channels?.max || 0}
                  </span>
                </div>
                {!planLimits.channels?.can_add && (
                  <Badge variant="outline" className="mt-2 text-amber-600 border-amber-300 text-xs">
                    Limit reached
                  </Badge>
                )}
              </div>

              {/* Users Usage */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium">Team Members</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold">{planLimits.users?.current || 0}</span>
                  <span className="text-muted-foreground">
                    / {planLimits.users?.max === -1 ? '∞' : planLimits.users?.max || 0}
                  </span>
                </div>
                {!planLimits.users?.can_add && (
                  <Badge variant="outline" className="mt-2 text-amber-600 border-amber-300 text-xs">
                    Limit reached
                  </Badge>
                )}
              </div>

              {/* Credits Usage */}
              {creditUsage && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Coins className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium">AI Credits</span>
                    <InfoTooltip content="Credits are used per AI interaction cycle." />
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold">{creditUsage.ai_credits?.remaining?.toLocaleString() || 0}</span>
                    <span className="text-muted-foreground">
                      / {creditUsage.ai_credits?.monthly_included?.toLocaleString() || 0}
                    </span>
                  </div>
                  {creditUsage.ai_credits?.warning && (
                    <Badge variant="outline" className="mt-2 text-amber-600 border-amber-300 text-xs">
                      Low credits
                    </Badge>
                  )}
                </div>
              )}
            </div>
          )}

          {/* All AI Features Note */}
          <Alert className="bg-emerald-50 border-emerald-200">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800">
              <span className="font-medium">All AI features are included in every plan.</span>
              {' '}Smart auto-replies, FAQ automation, structured intake flows, and more.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* AI Credit Wallet */}
      {creditUsage && (
        <Card className={creditUsage.ai_credits?.warning ? 'border-yellow-400' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-purple-600" />
              AI Credit Wallet
              {creditUsage.ai_credits?.warning && (
                <AlertTriangle className="w-4 h-4 text-yellow-600 ml-2" />
              )}
            </CardTitle>
            <CardDescription>
              Manage your AI credits for automated responses and workflows
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Credit Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Available Credits</p>
                <p className="text-2xl font-bold text-purple-600">
                  {creditUsage.ai_credits?.remaining?.toLocaleString() || 0}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Monthly Included</p>
                <p className="text-2xl font-bold">
                  {creditUsage.ai_credits?.monthly_included?.toLocaleString() || 0}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Purchased</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {creditUsage.ai_credits?.purchased_total?.toLocaleString() || 0}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Used Total</p>
                <p className="text-2xl font-bold text-slate-600">
                  {creditUsage.ai_credits?.used_total?.toLocaleString() || 0}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Credit Usage</span>
                <span className={creditUsage.ai_credits?.warning ? 'text-yellow-600 font-medium' : ''}>
                  {creditUsage.ai_credits?.remaining?.toLocaleString() || 0} remaining
                </span>
              </div>
              <Progress 
                value={Math.min(100, ((creditUsage.ai_credits?.remaining || 0) / (creditUsage.ai_credits?.monthly_included || 1)) * 100)}
                className={`h-3 ${creditUsage.ai_credits?.warning ? 'bg-yellow-100' : ''}`}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Resets on {creditUsage.plan?.reset_date ? new Date(creditUsage.plan.reset_date).toLocaleDateString() : 'next billing cycle'}
              </p>
            </div>

            {/* Top-up Packages */}
            {canPurchase && creditUsage.topup_packages && (
              <div>
                <h4 className="font-medium mb-3">Top Up Credits</h4>
                <div className="grid grid-cols-3 gap-4">
                  {creditUsage.topup_packages.map((pkg) => (
                    <div 
                      key={pkg.id}
                      className="p-4 border rounded-lg text-center hover:border-purple-400 transition-colors"
                    >
                      <p className="text-2xl font-bold text-purple-600">{pkg.credits?.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground mb-3">credits</p>
                      <Button 
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => handlePurchaseCredits(pkg.id)}
                        disabled={purchasingCredits === pkg.id}
                        data-testid={`buy-${pkg.id}`}
                      >
                        {purchasingCredits === pkg.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          pkg.display_price
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!canPurchase && (
              <p className="text-sm text-muted-foreground text-center py-2">
                Contact your organization Owner or Admin to purchase additional credits.
              </p>
            )}

            {/* Credit Transaction History */}
            {creditTransactions.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">Credit History</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {creditTransactions.slice(0, 5).map((tx) => (
                    <div 
                      key={tx.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm"
                    >
                      <div>
                        <span className="font-medium text-emerald-600">+{tx.credits_added?.toLocaleString()} credits</span>
                        <span className="text-muted-foreground ml-2 capitalize">{tx.source?.replace('_', ' ')}</span>
                      </div>
                      <span className="text-muted-foreground">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Available Plans</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative ${plan.popular ? 'border-2 border-emerald-500' : ''}`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              <CardHeader>
                <div className="flex items-center gap-2 mb-1">
                  <plan.icon className={`w-5 h-5 ${plan.popular ? 'text-emerald-600' : 'text-slate-500'}`} />
                  <CardTitle>{plan.name}</CardTitle>
                </div>
                <CardDescription>
                  <span className="text-3xl font-bold text-foreground">${plan.price}</span>
                  <span className="text-muted-foreground">/month</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={`flex items-center gap-2 text-sm ${feature.highlight ? 'text-emerald-700 font-medium' : ''}`}>
                      <Check className={`w-4 h-4 ${feature.highlight ? 'text-emerald-600' : 'text-emerald-500'}`} />
                      <span>
                        {feature.text}
                        {feature.tooltip && <InfoTooltip content={feature.tooltip} />}
                      </span>
                    </li>
                  ))}
                </ul>
                
                {currentPlan === plan.id || (currentPlan === 'professional' && plan.id === 'growth') ? (
                  <Button disabled className="w-full" variant="outline">
                    Current Plan
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={upgrading}
                    className={`w-full ${plan.popular ? 'bg-emerald-700 hover:bg-emerald-800' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    data-testid={`upgrade-${plan.id}`}
                  >
                    {upgrading ? 'Processing...' : currentPlan === 'trial' ? 'Start Plan' : 'Upgrade'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Enterprise Card */}
        <Card className="mt-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="plan-enterprise">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-semibold">Enterprise</h3>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  For large organizations with custom requirements.
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-slate-300">
                  <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-400" /> Custom Channels</span>
                  <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-400" /> Custom Users</span>
                  <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-400" /> Custom Credits</span>
                  <span className="flex items-center gap-1 text-emerald-400"><Check className="w-4 h-4" /> All AI features</span>
                </div>
              </div>
              <Link to="/dashboard/support">
                <Button className="bg-white text-slate-900 hover:bg-slate-100">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Transaction History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No transactions yet</p>
          ) : (
            <div className="space-y-4">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between p-4 bg-muted rounded-lg" data-testid={`transaction-${tx.id}`}>
                  <div>
                    <p className="font-medium">{getPlanDisplayName(tx.plan)} Plan</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">${tx.amount?.toFixed(2)} {tx.currency?.toUpperCase()}</p>
                    <Badge variant={tx.payment_status === 'paid' ? 'default' : 'secondary'} className={tx.payment_status === 'paid' ? 'bg-emerald-600' : ''}>
                      {tx.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer Note */}
      <p className="text-center text-muted-foreground text-xs">
        WhatsApp conversation fees are billed directly by Meta.
      </p>
    </div>
  );
};

export default BillingPage;
